/** El estado del espacio de trabajo que comparten la barra, la paleta y las páginas.
 *
 * El árbol vive aquí (y no en cada pantalla) porque lo tocan tres sitios a la vez: la barra lo
 * dibuja, la página cambia su título al escribir y la paleta crea páginas. Con una sola copia,
 * escribir un título lo actualiza en la barra en el mismo instante, sin volver a pedirlo.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import { usePortal } from '../contexto'
import type { Persona } from '../tipos'
import type { Nodo, NuevaPagina, Pagina } from './tipos'

interface Valor {
  arbol: Nodo[]
  cargado: boolean
  hijos: (padre: number | null) => Nodo[]
  favoritas: Nodo[]
  rutaPaginas: string
  /** Las personas del portal, para asignar en las bases de datos. */
  personas: Persona[]
  recargar: () => Promise<void>
  crear: (datos?: NuevaPagina) => Promise<Pagina | null>
  mover: (id: number, padre: number | null, posicion?: number) => Promise<void>
  alternarFavorita: (id: number) => Promise<void>
  aLaPapelera: (id: number) => Promise<void>
  duplicar: (id: number) => Promise<void>
  /** Cambia el nodo en local, al instante: el título que se está escribiendo sale en la barra. */
  tocarNodo: (id: number, parcial: Partial<Nodo>) => void
  expandidos: Set<number>
  alternarExpandido: (id: number, valor?: boolean) => void
  barraAbierta: boolean
  alternarBarra: () => void
  paletaAbierta: boolean
  abrirPaleta: () => void
  cerrarPaleta: () => void
  papeleraAbierta: boolean
  abrirPapelera: (valor: boolean) => void
}

const Contexto = createContext<Valor | null>(null)

export function useEspacio(): Valor {
  const v = useContext(Contexto)
  if (!v) throw new Error('useEspacio fuera del proveedor')
  return v
}

function leer<T>(clave: string, defecto: T): T {
  try {
    const crudo = localStorage.getItem(clave)
    return crudo ? (JSON.parse(crudo) as T) : defecto
  } catch {
    return defecto
  }
}

function guardar(clave: string, valor: unknown) {
  // Una comodidad por navegador: si el almacenamiento está bloqueado, todo sigue funcionando.
  try {
    localStorage.setItem(clave, JSON.stringify(valor))
  } catch {
    /* modo privado */
  }
}

export function ProveedorDelEspacio({ children }: { children: ReactNode }) {
  const { ruta, servidor, avisar, puedo } = usePortal()
  // Quien sólo tiene acceso a solicitudes no carga ni páginas ni personas: darían un error.
  const hayEspacio = puedo('ver_espacio')
  const navegar = useNavigate()
  const rutaPaginas = `${ruta}/paginas`
  const base = `/${servidor.id}`

  const [arbol, setArbol] = useState<Nodo[]>([])
  const [cargado, setCargado] = useState(false)
  const [expandidos, setExpandidos] = useState<Set<number>>(
    () => new Set(leer<number[]>(`espacio.${servidor.id}.abiertos`, [])),
  )
  const [barraAbierta, setBarraAbierta] = useState(() =>
    leer<boolean>('espacio.barra', window.innerWidth > 900),
  )
  const [paletaAbierta, setPaletaAbierta] = useState(false)
  const [papeleraAbierta, abrirPapelera] = useState(false)
  const [personas, setPersonas] = useState<Persona[]>([])

  const recargar = useCallback(async () => {
    if (!hayEspacio) {
      setCargado(true)
      return
    }
    try {
      setArbol(await api.get<Nodo[]>(rutaPaginas))
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : 'No se han podido cargar las páginas.', 'malo')
    } finally {
      setCargado(true)
    }
  }, [rutaPaginas, avisar, hayEspacio])

  useEffect(() => {
    void recargar()
  }, [recargar])

  useEffect(() => {
    if (!hayEspacio) return
    api.get<Persona[]>(`${rutaPaginas}/personas`).then(setPersonas).catch(() => setPersonas([]))
  }, [rutaPaginas, hayEspacio])

  const hijos = useCallback(
    (padre: number | null) =>
      arbol
        .filter((n) => n.parent_id === padre)
        .sort((a, b) => a.position - b.position || a.id - b.id),
    [arbol],
  )

  const favoritas = useMemo(() => arbol.filter((n) => n.favorita), [arbol])

  const alternarExpandido = useCallback(
    (id: number, valor?: boolean) => {
      setExpandidos((previos) => {
        const nuevos = new Set(previos)
        const abrir = valor ?? !nuevos.has(id)
        if (abrir) nuevos.add(id)
        else nuevos.delete(id)
        guardar(`espacio.${servidor.id}.abiertos`, [...nuevos])
        return nuevos
      })
    },
    [servidor.id],
  )

  const tocarNodo = useCallback((id: number, parcial: Partial<Nodo>) => {
    setArbol((previo) => previo.map((n) => (n.id === id ? { ...n, ...parcial } : n)))
  }, [])

  const fallo = useCallback(
    (e: unknown, defecto: string) => {
      avisar(e instanceof ErrorDeApi ? e.message : defecto, 'malo')
    },
    [avisar],
  )

  const crear = useCallback(
    async (datos: NuevaPagina = {}) => {
      try {
        const pagina = await api.post<Pagina>(rutaPaginas, datos)
        await recargar()
        if (datos.parent_id != null) alternarExpandido(datos.parent_id, true)
        navegar(`${base}/paginas/${pagina.id}`)
        return pagina
      } catch (e) {
        fallo(e, 'No se ha podido crear la página.')
        return null
      }
    },
    [rutaPaginas, recargar, alternarExpandido, navegar, base, fallo],
  )

  const mover = useCallback(
    async (id: number, padre: number | null, posicion?: number) => {
      const cuerpo: Record<string, unknown> = { parent_id: padre }
      if (posicion !== undefined) cuerpo.position = posicion
      try {
        await api.patch(`${rutaPaginas}/${id}`, cuerpo)
        if (padre != null) alternarExpandido(padre, true)
      } catch (e) {
        fallo(e, 'No se ha podido mover la página.')
      }
      await recargar()
    },
    [rutaPaginas, recargar, alternarExpandido, fallo],
  )

  const alternarFavorita = useCallback(
    async (id: number) => {
      const actual = arbol.find((n) => n.id === id)?.favorita ?? false
      tocarNodo(id, { favorita: !actual })
      try {
        await api.put(`${rutaPaginas}/${id}/favorita`, { valor: !actual })
      } catch (e) {
        tocarNodo(id, { favorita: actual })
        fallo(e, 'No se ha podido cambiar el favorito.')
      }
    },
    [arbol, rutaPaginas, tocarNodo, fallo],
  )

  const aLaPapelera = useCallback(
    async (id: number) => {
      const nodo = arbol.find((n) => n.id === id)
      try {
        await api.borrar(`${rutaPaginas}/${id}`)
        avisar(`«${nodo?.title || 'Sin título'}» está en la papelera.`)
        await recargar()
        // Si se estaba viendo esa página (o una que colgaba de ella), se sale de ahí.
        if (window.location.pathname.startsWith(`${base}/paginas/`)) {
          navegar(`${base}/paginas`, { replace: true })
        }
      } catch (e) {
        fallo(e, 'No se ha podido borrar la página.')
      }
    },
    [arbol, rutaPaginas, avisar, recargar, navegar, base, fallo],
  )

  const duplicar = useCallback(
    async (id: number) => {
      try {
        const copia = await api.post<Pagina>(`${rutaPaginas}/${id}/duplicar`)
        await recargar()
        navegar(`${base}/paginas/${copia.id}`)
      } catch (e) {
        fallo(e, 'No se ha podido duplicar la página.')
      }
    },
    [rutaPaginas, recargar, navegar, base, fallo],
  )

  const alternarBarra = useCallback(() => {
    setBarraAbierta((v) => {
      guardar('espacio.barra', !v)
      return !v
    })
  }, [])

  // Atajos globales: Ctrl/Cmd+K abre la paleta, Ctrl/Cmd+\ pliega la barra.
  useEffect(() => {
    if (!hayEspacio) return
    const alPulsar = (e: KeyboardEvent) => {
      const mando = e.ctrlKey || e.metaKey
      if (mando && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletaAbierta((v) => !v)
      } else if (mando && e.key === '\\') {
        e.preventDefault()
        alternarBarra()
      }
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [alternarBarra, hayEspacio])

  const valor = useMemo<Valor>(
    () => ({
      arbol, cargado, hijos, favoritas, rutaPaginas, personas, recargar, crear, mover,
      alternarFavorita, aLaPapelera, duplicar, tocarNodo, expandidos, alternarExpandido,
      barraAbierta, alternarBarra, paletaAbierta,
      abrirPaleta: () => setPaletaAbierta(true),
      cerrarPaleta: () => setPaletaAbierta(false),
      papeleraAbierta, abrirPapelera,
    }),
    [arbol, cargado, hijos, favoritas, rutaPaginas, personas, recargar, crear, mover, alternarFavorita,
      aLaPapelera, duplicar, tocarNodo, expandidos, alternarExpandido, barraAbierta,
      alternarBarra, paletaAbierta, papeleraAbierta],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}
